import { api } from './api';
import { secureStorageGet, secureStorageRemove } from './encryption';

let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

// Store the refresh function - will be set by AuthContext
let refreshTokenFn: (() => Promise<boolean>) | null = null;

export const setRefreshTokenFunction = (refreshFn: () => Promise<boolean>) => {
  refreshTokenFn = refreshFn;
};

// Enhanced response interceptor that handles token refresh
api.interceptors.response.use(
  response => {
    // Log successful responses in development
    if (import.meta.env.DEV) {
      console.log(
        `[API Response] ${response.config?.method?.toUpperCase()} ${response.config?.url}`,
        {
          status: response.status,
          data: response.data,
        }
      );
    }
    // Return just the data to maintain consistency
    return response.data;
  },
  async error => {
    const status = error.response?.status;
    const errorData = error.response?.data;
    const method = error.config?.method?.toUpperCase();
    const url = error.config?.url;

    // Enhanced logging for all errors
    console.error(`[API Error] ${method} ${url}`, {
      status,
      statusText: error.response?.statusText,
      data: errorData,
      message: error.message,
    });

    // Handle 401 errors with token refresh
    if (
      status === 401 &&
      refreshTokenFn &&
      !url?.includes('/auth/refresh') &&
      !url?.includes('/auth/login')
    ) {
      console.log('[Auth] Received 401, attempting token refresh...');

      // Prevent multiple refresh attempts
      if (!isRefreshing) {
        isRefreshing = true;
        refreshPromise = refreshTokenFn();
      }

      try {
        const refreshSuccess = await refreshPromise;

        if (refreshSuccess) {
          console.log('[Auth] Token refreshed, retrying original request');
          // Update the authorization header with the new token
          const newToken = secureStorageGet('token');
          if (newToken && error.config) {
            error.config.headers.Authorization = `Bearer ${newToken}`;
            // Retry the original request
            return api.request(error.config);
          }
        } else {
          console.log('[Auth] Token refresh failed, redirecting to login');
          // Clear tokens and redirect
          secureStorageRemove('token');
          secureStorageRemove('refreshToken');
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
      } catch (refreshError) {
        console.error('[Auth] Token refresh error:', refreshError);
        secureStorageRemove('token');
        secureStorageRemove('refreshToken');
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      } finally {
        isRefreshing = false;
        refreshPromise = null;
      }
    } else if (status === 401) {
      // For login/refresh endpoints or when no refresh function is available
      console.log('[Auth] Unauthorized - clearing token and redirecting');
      secureStorageRemove('token');
      secureStorageRemove('refreshToken');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }

    if (status === 429) {
      console.warn(
        '[Rate Limit] Too many requests - user should wait before retrying'
      );
    }

    const errorMessage =
      errorData?.error || errorData?.message || 'An error occurred';
    return Promise.reject(new Error(errorMessage));
  }
);

export { api };
